/* eslint-disable no-self-assign */
/* eslint-disable @typescript-eslint/camelcase, @typescript-eslint/no-use-before-define */
import { get, set } from 'lodash'
import intl from 'react-intl-universal'
import ReactDOM from 'react-dom'
import React from 'react'
import { Provider } from 'react-redux'

import ArcUUID from '~/util/arc-identifiers'
import { isCustomEmbedByKey, getSubtype } from '~/util/customEmbed'
import CustomEmbedModal from '~/reactapp/App/ModalContext/CustomEmbedModal/CustomEmbedModal'
import AnglerfishModal from '~/reactapp/App/ModalContext/AnglerfishModal'
import { setStoryAns } from '~/redux/storyData'

/* GLOBALS $ */
export default [
  '$rootScope',
  '$scope',
  'storyService',
  'thirdPartyIntegrationService',
  'ansService',
  'toastr',
  '$ngRedux',
  '$timeout',
  promoBodyController,
]

const integrationUrls = {
  image: '/photo/selector/',
  video: '/videocenter/plugins/videosearch/',
  gallery: '/photo/galleries/selector/',
  raw_html: {
    content: '',
  },
}

function promoBodyController(
  $rootScope,
  $scope,
  storyService,
  thirdPartyIntegrationService,
  ansService,
  toastr,
  $ngRedux,
  $timeout
) {
  this.intl = intl
  let reactRenderContainer
  let ngReduxUnsubscribe

  const updateVisibleSections = () => {
    this.filteredPromoSections = Object.entries(this.promo_items).reduce(
      (featuredMedias, [key, ansValue]) => {
        if (Object.keys(ansValue).length) {
          // forward compatibility
          // https://github.com/washingtonpost/ans-schema/blob/master/src/main/resources/schema/ans/0.10.9/image.json
          if (ansValue.type === 'image' && ansValue.focal_point) {
            if (!ansValue.additional_properties) {
              ansValue.additional_properties = {}
            }
            ansValue.additional_properties.focal_point = {
              min: [ansValue.focal_point.x, ansValue.focal_point.y],
              max: [ansValue.focal_point.x, ansValue.focal_point.y],
            }
            if (!ansValue.referent_properties) {
              ansValue.referent_properties = {}
            }
            if (!ansValue.referent_properties.additional_properties) {
              ansValue.referent_properties.additional_properties = {}
            }
            ansValue.referent_properties.additional_properties.focal_point = {
              min: [ansValue.focal_point.x, ansValue.focal_point.y],
              max: [ansValue.focal_point.x, ansValue.focal_point.y],
            }
          }
          featuredMedias[key] = ansValue || 'image'
        }
        return featuredMedias
      },
      {}
    )
    this.hasImage = Object.entries(this.promo_items).some(
      ([, ansValue]) => ansValue.type === 'image'
    )
  }

  const addPromoItem = (key, promoItem) => {
    this.promo_items[key] = promoItem
    // Since raw_html is the only non-reference type, do not add referent_properties
    if (promoItem.type !== 'raw_html' && promoItem.type !== 'custom_embed') {
      promoItem.referent_properties = promoItem.referent_properties || {}
    }
    this.updateRedux()
  }

  const handleIntegrationData = (key, data) => {
    const ans = ansService.convertToReference(
      data instanceof Array ? data : [data]
    )

    ansService
      .denormalize(ans)
      .then((items) => {
        items.forEach((promoItem) => {
          if (promoItem.type === 'reference') {
            throw new Error('Promo item denormalization error')
          }
          addPromoItem(key, promoItem)
        })
        updateVisibleSections()
      })
      .catch((error) => {
        console.log(error)
        toastr.error(
          intl.get('promoBody.error.denormalization'),
          intl.get('common.error')
        )
        return null
      })
  }

  const addPromoItemThroughIntegration = (key, url) => {
    thirdPartyIntegrationService(url)
      .then((data) => {
        handleIntegrationData(key, data)
      })
      .catch(() => {
        // User cancel
      })
  }

  const mediaActionHandlers = {
    preview: ({ name }) => {
      const ans = this.promo_items[name]
      window.open(
        ans.canonical_url || ans.additional_properties.proxyUrl,
        '_blank'
      )
    },
    clear: ({ name }) => {
      delete this.promo_items[name]
      this.updateRedux()
      updateVisibleSections()
    },
    edit: ({ name }) => {
      const ans = this.promo_items[name]
      window.open(`/photo/${ans._id}`, '_blank')
    },
    select: ({ type, name }) => {
      if (!integrationUrls[type]) {
        toastr.error(
          intl.get('promoBody.error.type', { type }),
          intl.get('common.error')
        )
        return
      }
      const integration = integrationUrls[type]

      if (typeof integration === 'object') {
        addPromoItem(name, {
          ...integration,
          _id: ArcUUID.create(),
          type,
        })
        updateVisibleSections()
      } else if (type === 'image' || type === 'gallery') {
        renderAnglerfishModal({
          visible: true,
          contentType: type,
          onSelect: (data) => {
            handleIntegrationData(name, data)
          },
        })
      } else {
        addPromoItemThroughIntegration(name, integration)
      }
    },
  }

  const renderAnglerfishModal = ({ visible, contentType, onSelect }) => {
    const close = () => {
      renderAnglerfishModal({ visible: false, contentType, onSelect })
    }

    ReactDOM.render(
      <Provider store={$ngRedux}>
        <AnglerfishModal
          visible={visible}
          contentType={contentType}
          onClose={close}
          onSelect={(data) => {
            onSelect(data)
            close()
          }}
        />
      </Provider>,
      reactRenderContainer
    )
  }

  const closeCustomEmbedModal = () => {
    renderCustomEmbedModal({ visible: false })
  }

  const renderCustomEmbedModal = ({ visible, subtype, onData }) => {
    ReactDOM.render(
      visible && (
        <Provider store={$ngRedux}>
          <CustomEmbedModal
            mode="search"
            store={$ngRedux}
            visible={visible}
            onClose={closeCustomEmbedModal}
            onData={onData}
            subtype={subtype}
          />
        </Provider>
      ),
      reactRenderContainer
    )
  }

  $scope.$on('$destroy', () => {
    ngReduxUnsubscribe()
    ReactDOM.unmountComponentAtNode(reactRenderContainer)
  })

  $rootScope.$on('DISPATCH', (info, action) => {
    if (action.type === 'REQUIRED_FIELDS') {
      this.isPromoRequired = !!action.fields.find(
        (o) => o.key === 'promo_items'
      )
    }
  })

  this.$onInit = () => {
    ngReduxUnsubscribe = $ngRedux.connect((state) => ({
      customEmbedTypes: state.settings.customEmbed,
    }))(this)

    this.promo_items = this.storyObj.promo_items
    this.companySettings = this.companySettingsObj || {}
    this.hasFeaturedMediaSetting = !!this.companySettings.featuredMedia
    this.hasImage = false
    const mediaTypeOptions = this.companySettings.featuredMedia
      ? [
          ...new Set(
            Object.values(this.companySettings.featuredMedia).flatMap(
              (item) => item.types
            )
          ),
        ]
      : []
    this.mediaTypeOptions = mediaTypeOptions.reduce((result, key) => {
      let label
      if (isCustomEmbedByKey(key)) {
        const subtype = getSubtype(key)
        label = get(
          this,
          `companySettings.customEmbed[${subtype}].caption`,
          subtype
        )
      } else {
        label = intl.get(`featuredMedia.mediaTypes.${key}`).defaultMessage(key)
      }
      return {
        ...result,
        [key]: label,
      }
    }, {})

    // Auto-select Media Type if only one option is available, and generate selected Media Key options
    if (mediaTypeOptions.length === 1) {
      $scope.selectedMediaType = mediaTypeOptions[0]
      this.handleMediaTypeChange(mediaTypeOptions[0])
    } else {
      this.mediaKeyOptions = []
    }
    updateVisibleSections()
  }

  this.$postLink = () => {
    $timeout(() => {
      // Do something with the DOM now that it has had it's bindings applied,
      // otherwise getElementById will return null.
      // https://stackoverflow.com/a/37306971
      reactRenderContainer = document.getElementById('react_custom-embed-modal')
    })
  }

  this.handleMediaTypeChange = (selectedMediaType) => {
    this.mediaKeyOptions = Object.entries(
      this.companySettings.featuredMedia
    ).reduce((acc, [key, value]) => {
      const nextAcc = [...acc]

      if (value.types.includes(selectedMediaType)) {
        nextAcc.push(key)
      }

      return nextAcc
    }, [])
    // Auto-select Media Key when only single option available
    if (this.mediaKeyOptions.length === 1) {
      $scope.selectedMediaKey = this.mediaKeyOptions[0]
    }
  }

  this.handleAddMedia = (type, mediaKey) => {
    const isCustomEmbed =
      this.customEmbedTypes && isCustomEmbedByKey(type)
        ? Object.keys(this.customEmbedTypes).includes(getSubtype(type))
        : false

    if (isCustomEmbed) {
      const subtype = getSubtype(type)
      renderCustomEmbedModal({
        visible: true,
        subtype,
        onData: (data) => {
          addPromoItem(mediaKey, {
            _id: ArcUUID.create(),
            subtype,
            type: 'custom_embed',
            embed: data,
          })
          updateVisibleSections()
          closeCustomEmbedModal()
        },
      })
    } else {
      mediaActionHandlers.select({ type, name: mediaKey })
    }
  }

  this.setPromoFocal = (section, ans) => {
    $rootScope.$broadcast('DISPATCH', {
      type: 'HANDLE_COMMANDS',
      sub_type: 'set-focal-point',
      feature: ans,
      featureAnsPath: `promo_items.${section}`,
    })
  }

  this.ANSUrlGetters = {
    image: (ans) => get(ans, 'additional_properties.proxyUrl') || ans.url,
    gallery: (ans) =>
      get(ans, 'promo_items.basic.additional_properties.proxyUrl') ||
      get(ans, 'content_elements[0].additional_properties.proxyUrl') ||
      get(ans, 'content_elements[0].url') ||
      get(ans, 'promo_items.basic.url'),
    video: (ans) => get(ans, 'promo_image.url'),
  }

  this.handleMediaAction = (payload) => {
    mediaActionHandlers[payload.action](payload)
  }

  this.edit = (ans) => window.open(`/photo/${ans._id}`, '_blank')

  this.setEmbedValue = (section, embed) => {
    this.promo_items[section].embed = embed
    this.updateRedux()
  }

  this.checkDefaultOptionSelected = (section) => {
    const updatedValue = this.promo_items[section].subtype

    if (updatedValue === null) {
      // default option selected
      delete this.promo_items[section].subtype

      if (
        this.promo_items[section].referent_properties &&
        this.promo_items[section].referent_properties.subtype
      ) {
        delete this.promo_items[section].referent_properties.subtype
      }
    } else {
      set(
        this.promo_items[section],
        'referent_properties.subtype',
        updatedValue
      )
    }

    this.updateRedux()
  }

  this.updateRedux = () => {
    $ngRedux.dispatch(
      setStoryAns({
        storyAns: {
          ...this.storyObj,
          promo_items: this.promo_items,
          isDirty: true,
        },
        isDirty: true,
      })
    )
  }
}
